import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useJamRoom } from '../hooks/useJamRoom.jsx';
import { usePlaybackEngine } from '../hooks/usePlaybackEngine.js';
import { fmtDuration, fmtTime } from '../lib/format.js';
import TrackCover from './TrackCover.jsx';
import {
  IconNext,
  IconPause,
  IconPlay,
  IconPrev,
  IconSync,
  IconVolume,
  IconVolumeMute,
} from './icons.jsx';

/**
 * Barra de reproduccion anclada al fondo (Material 3):
 *  - <audio> oculto gestionado por usePlaybackEngine;
 *  - progreso del reloj interpolado; se congela mientras el stream carga;
 *  - seek y play/pause solo para el anfitrion; volumen local persistido.
 */
export default function PlayerBar() {
  const jam = useJamRoom();
  const { currentTrack, playback, isHost, control } = jam;
  const audioRef = useRef(null);
  const engine = usePlaybackEngine(jam, audioRef);

  /* Volumen local persistido. */
  const [volume, setVolume] = useState(() => {
    try {
      const stored = Number(localStorage.getItem('jam:volume'));
      return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : 0.9;
    } catch {
      return 0.9;
    }
  });
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = Math.min(1, Math.max(0, volume));
      audio.muted = muted;
    }
  }, [volume, muted]);

  useEffect(() => {
    try {
      localStorage.setItem('jam:volume', String(volume));
    } catch {
      /* sin persistencia */
    }
  }, [volume]);

  /* Ticker de 300 ms para el progreso. */
  const [, setTick] = useState(0);
  const playing = !!playback?.playing;
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => setTick((v) => v + 1), 300);
    return () => clearInterval(id);
  }, [playing]);

  const now = jam.getPlaybackNow();
  const duration = Math.max(
    0,
    currentTrack?.durationSec > 0 ? currentTrack.durationSec : playback?.durationSec ?? 0,
  );

  // La barra solo refleja audio REAL: durante la carga se congela (0 al cambiar
  // de pista); ya lista sigue al <audio>; en pausa manda el reloj del servidor.
  const trackKey = currentTrack?.videoId ?? null;
  const posRef = useRef({ trackKey: null, pos: 0 });
  const loading = engine.engineState === 'loading';
  const audioEl = audioRef.current;
  const audioPos = audioEl && Number.isFinite(audioEl.currentTime) ? audioEl.currentTime : 0;
  let livePos;
  if (posRef.current.trackKey !== trackKey) {
    posRef.current = { trackKey, pos: 0 };
    livePos = 0;
  } else if (loading) {
    livePos = posRef.current.pos;
  } else {
    livePos = !playing
      ? now?.positionSec ?? 0
      : engine.engineState === 'ready'
        ? audioPos
        : now?.positionSec ?? 0;
    posRef.current.pos = livePos;
  }

  const [dragPos, setDragPos] = useState(null);
  const shownPos = dragPos ?? livePos;
  const sliderMax = duration > 0 ? duration : 1;
  const sliderValue = Math.min(Math.max(shownPos, 0), sliderMax);

  async function togglePlay() {
    if (!(duration > 0) && jam.queue.length === 0) return;
    try {
      if (playing) {
        await control('pause', { positionSec: audioRef.current?.currentTime ?? undefined });
      } else {
        await control('play', {
          positionSec: audioRef.current?.paused ? audioRef.current.currentTime : undefined,
        });
      }
    } catch {
      /* el provider tostea errores */
    }
  }

  const controlsLocked = !isHost;
  const track = currentTrack;

  return (
    <Paper
      component="footer"
      elevation={12}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar,
        overflow: 'hidden',
        borderTop: '1px solid',
        borderColor: 'divider',
        borderTopLeftRadius: { xs: '22px', md: 0 },
        borderTopRightRadius: { xs: '22px', md: 0 },
        bgcolor: 'rgba(24,25,26,0.94)',
        backdropFilter: 'blur(24px)',
        px: { xs: 1.5, sm: 2, md: 3 },
        pt: { xs: 1, md: 1.25 },
        pb: { xs: 'max(8px, env(safe-area-inset-bottom))', md: 1.25 },
      }}
    >
      <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />

      {engine.needGesture && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(19,19,20,0.74)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box sx={{ px: 2, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<IconPlay size={22} />}
              onClick={engine.resumeFromGesture}
            >
              Toca para transmitir
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              El navegador requiere tu toque para reproducir audio.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          alignItems: 'center',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr) auto',
            md: 'minmax(190px, 280px) auto minmax(220px, 1fr) auto',
          },
          gridTemplateAreas: {
            xs: '"info controls" "progress progress"',
            md: '"info controls progress actions"',
          },
          columnGap: { xs: 1, md: 2 },
          rowGap: { xs: 0.75, md: 0 },
        }}
      >
        {/* Pista en curso */}
        <Box sx={{ gridArea: 'info', display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <TrackCover src={track?.thumbnail} alt={track?.title} size="md" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {track ? track.title : 'Cola vacía'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ lineHeight: 1.3 }}>
              {track ? track.artist : 'Añade una canción para empezar'}
            </Typography>
          </Box>
        </Box>

        {/* Controles */}
        <Box
          sx={{
            gridArea: 'controls',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.25, md: 0.5 },
            flex: '0 0 auto',
          }}
        >
          {isHost && (
            <Tooltip title="Anterior (reinicia si ya avanzó)">
              <span>
                <IconButton
                  aria-label="Canción anterior"
                  onClick={() => control('prev').catch(() => {})}
                  disabled={!track}
                  sx={{ p: { xs: 0.75, md: 1 } }}
                >
                  <IconPrev size={22} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <IconButton
            onClick={togglePlay}
            disabled={!track || controlsLocked}
            aria-label={playing ? 'Pausa' : 'Reproducir'}
            sx={{
              width: { xs: 44, md: 52 },
              height: { xs: 44, md: 52 },
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.light' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' },
            }}
          >
            {playing ? <IconPause size={26} /> : <IconPlay size={26} />}
          </IconButton>

          {isHost && (
            <Tooltip title="Siguiente">
              <span>
                <IconButton
                  aria-label="Siguiente canción"
                  onClick={() => control('next').catch(() => {})}
                  disabled={!track}
                  sx={{ p: { xs: 0.75, md: 1 } }}
                >
                  <IconNext size={22} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {engine.engineState === 'loading' && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
          {engine.engineState === 'error' && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="Reintentar audio"
              onClick={engine.retryLoad}
              sx={{ ml: 0.5 }}
            />
          )}
        </Box>

        {/* Progreso */}
        <Box
          sx={{
            gridArea: 'progress',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 1.5 },
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right', flex: '0 0 auto' }}
          >
            {fmtTime(shownPos)}
          </Typography>
          <Slider
            size="small"
            min={0}
            max={sliderMax}
            step={0.5}
            value={sliderValue}
            onChange={(_e, value) => setDragPos(value)}
            onChangeCommitted={(_e, value) => {
              setDragPos(null);
              if (isHost && duration > 0) control('seek', { positionSec: value }).catch(() => {});
            }}
            disabled={!isHost || !(duration > 0)}
            aria-label="Progreso de la canción"
            sx={{ flex: 1 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 34, flex: '0 0 auto' }}
          >
            {fmtDuration(duration)}
          </Typography>
        </Box>

        {/* Acciones secundarias (escritorio) */}
        <Box
          sx={{
            gridArea: 'actions',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 0.5,
            flex: '0 0 auto',
          }}
        >
          <Tooltip title="Sincronizar con el anfitrión">
            <IconButton
              size="small"
              aria-label="Sincronizar con el anfitrión"
              onClick={() => jam.syncRequest().catch(() => {})}
            >
              <IconSync size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={muted ? 'Activar sonido' : 'Silenciar'}>
            <IconButton
              size="small"
              aria-label={muted ? 'Activar sonido' : 'Silenciar'}
              onClick={() => setMuted((m) => !m)}
            >
              {muted ? <IconVolumeMute size={20} /> : <IconVolume size={20} />}
            </IconButton>
          </Tooltip>
          <Slider
            size="small"
            min={0}
            max={1}
            step={0.02}
            value={muted ? 0 : volume}
            onChange={(_e, value) => {
              const next = Number(value);
              setVolume(next);
              if (next > 0) setMuted(false);
            }}
            aria-label="Volumen"
            sx={{ width: 90, ml: 0.5 }}
          />
        </Box>
      </Box>
    </Paper>
  );
}
